'use client';

import React, { useEffect, useRef, useState } from 'react';
import type Hls from 'hls.js';
import type { ErrorData } from 'hls.js';
import { ENV } from '@/ots-constants';
import { useServerOneTrustCookieGroups } from '@/settings/SettingsContext';
import { OneTrustCategories } from '@/tracking/onetrust/constants';
import { useOTCategoryStatus } from '@/tracking/onetrust/useOTStatus';
import { Diagnostics } from '@/util/diagnostics';
import { useReadyOnWebOnly } from '@/util/useReadyOnWebOnly';

// Live HLS webcam, layered over the poster photo inside ResortWebcam.
//
// Plays through hls.js whenever Media Source Extensions are available and
// only falls back to the browser's native HLS where they are not (iPhones
// before iOS 17.1). The order matters: desktop Chrome now reports native HLS
// support, but its native pipeline intermittently rejects the Wowza
// playlists these cams come from (see ./hlsSource.ts).
//
// Consent: the iframe embeds this replaces were injected under Targeting
// Cookies, so the stream is held to the same gate — a consent cookie present
// at request time, or consent granted in this session (the check
// usePageVariables in tracking/analytics uses). Local dev skips it, as
// useOneTrustIframe does. The player mounts only after hydration: the live
// status is parsed from document.cookie during the client's first render,
// so gating markup on it there could disagree with the server's
// request-cookie groups and throw away the server HTML.
//
// The <video> has no poster: it stays transparent until the first frame, so
// the card's photo shows through while the stream starts, and it is dropped
// on a fatal error so the photo is what remains.

const HLS_MIME = 'application/vnd.apple.mpegurl';

// A webcam page keeps ~10 live players open indefinitely; without a cap
// hls.js holds every second already played in the buffer.
const HLS_CONFIG = { backBufferLength: 30 };

export type LiveHlsStreamProps = {
  /** HLS playlist URL (see getHlsSource). */
  src: string;
  /** Accessible name for the video. */
  title: string;
};

const useTargetingConsent = () => {
  const preAllowed = useServerOneTrustCookieGroups()?.[OneTrustCategories.TargetingCookies];
  const live = useOTCategoryStatus(OneTrustCategories.TargetingCookies);
  return ENV === 'local' || preAllowed === true || live === true;
};

export const LiveHlsStream = ({ src, title }: LiveHlsStreamProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);
  const hasConsent = useTargetingConsent();
  const webReady = useReadyOnWebOnly();
  const active = webReady && hasConsent;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !active) return undefined;

    let cancelled = false;
    let hls: Hls | undefined;
    const stopHls = () => {
      hls?.destroy();
      hls = undefined;
    };
    const fail = (reason: unknown) => {
      if (cancelled) return;
      Diagnostics.message({ tag: 'LiveHlsStream', message: 'Stream failed, keeping the still', data: { src, reason } });
      setFailed(true);
    };
    const onNativeError = () => fail(video.error?.message);

    // React sets `muted` as a property only on client-side creation, not when
    // hydrating server markup; autoplay policies require it before play.
    video.muted = true;

    import('hls.js/light').then(({ default: HlsLight }) => {
      if (cancelled) return;
      if (HlsLight.isSupported()) {
        const player = new HlsLight(HLS_CONFIG);
        hls = player;
        let recoveredMedia = false;
        player.on(HlsLight.Events.ERROR, (_event, data: ErrorData) => {
          if (!data.fatal) return;
          // One recovery attempt for decode/buffer faults, as the hls.js docs
          // recommend; a playlist or segment that won't load is final.
          if (data.type === HlsLight.ErrorTypes.MEDIA_ERROR && !recoveredMedia) {
            recoveredMedia = true;
            player.recoverMediaError();
            return;
          }
          stopHls();
          fail(data.details);
        });
        player.loadSource(src);
        player.attachMedia(video);
      } else if (video.canPlayType(HLS_MIME)) {
        video.addEventListener('error', onNativeError);
        video.src = src;
      } else {
        fail('neither MSE nor native HLS');
      }
    }).catch(fail);

    return () => {
      cancelled = true;
      stopHls();
      video.removeEventListener('error', onNativeError);
      if (video.hasAttribute('src')) {
        video.removeAttribute('src');
        video.load();
      }
    };
  }, [src, active]);

  if (!active || failed) return null;

  return (
    // The feeds carry no audio track, so there is nothing to caption.
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      ref={videoRef}
      className="absolute inset-0 h-full w-full object-cover"
      aria-label={title}
      autoPlay
      muted
      playsInline
      controls
    />
  );
};
